import { apiEndpoint, getZaplifySdk } from '@zaplify/sdk';
import React, { createContext, useState } from 'react';
import { useDispatch } from 'react-redux-latest';
import { ActionState } from '../enums/actions';
import { UploadCompaniesBreadCrumbs } from '../enums/breadcrumbs';
import { setNotification } from '../redux/actions';
import { AppDispatch } from '../redux/store/configureStore';
import { ValidatedName } from '../types/companies';
import { CompanyAutocompleteOption } from '../types/option';
import { useMsal } from '@azure/msal-react';

type UploadCompaniesState = {
    currentView: UploadCompaniesBreadCrumbs;
    actionState: ActionState;
    csvData: { [key: string]: any }[];
    selectedColumn: { index: number; name: string } | null;
    selectedCompanies: CompanyAutocompleteOption[];
    isWaitingForValidation: boolean;
    loading: boolean;
};

type ContextProps = {
    currentView: UploadCompaniesBreadCrumbs;
    actionState: ActionState;
    selectedColumn: string | null;
    csvData: { [key: string]: any }[];
    selectedCompanies: CompanyAutocompleteOption[];
    isWaitingForValidation: boolean;
    loading: boolean;
    handleDeleteValidatedCompany: (companyName: string) => void;
    handleViewChange: (view: UploadCompaniesBreadCrumbs) => void;
    handleActionStateChange: (newState: ActionState) => void;
    handleUpdateSelectedColumn: (name: string | null) => void;
    handleResetSelectedColumn: () => void;
    handleUpdateCsvData: (data: { [key: string]: any }[]) => void;
    handleUpdateSelectedCompany: (newCompany: CompanyAutocompleteOption, idx: number) => void;
    getValidationForCompanies: () => void;
    resetAllValues: () => void;
    handleLoadingChange: (value: boolean) => void;
};

export const UploadCompaniesContext = createContext<ContextProps>({
    currentView: UploadCompaniesBreadCrumbs.UPLOAD_LIST,
    actionState: ActionState.DISABLED,
    selectedColumn: null,
    csvData: [],
    selectedCompanies: [],
    isWaitingForValidation: false,
    loading: false,
    handleDeleteValidatedCompany: (companyName: string) => {},
    handleViewChange: (view: UploadCompaniesBreadCrumbs) => {},
    handleActionStateChange: (newState: ActionState) => {},
    handleUpdateSelectedColumn: (name: string | null) => {},
    handleResetSelectedColumn: () => {},
    handleUpdateCsvData: (data: { [key: string]: any }[]) => {},
    handleUpdateSelectedCompany: (newCompany: CompanyAutocompleteOption, idx: number) => {},
    getValidationForCompanies: () => {},
    resetAllValues: () => {},
    handleLoadingChange: (value: boolean) => {},
});

export const UploadCompaniesProvider = ({ children }: { children?: React.ReactNode }) => {
    const dispatch = useDispatch<AppDispatch>();
    const [currentView, setCurrentView] = useState<UploadCompaniesBreadCrumbs>(UploadCompaniesBreadCrumbs.UPLOAD_LIST);
    const [actionState, setActionState] = useState(ActionState.DISABLED);
    const [csvData, setCsvData] = useState<{ [key: string]: any }[]>([]);
    const [selectedColumn, setSelectedColumn] = useState<string | null>(null);
    const [selectedCompanies, setSelectedCompanies] = useState<CompanyAutocompleteOption[]>([]);
    const [isWaitingForValidation, setIsWaitingForValidation] = useState(false);
    const [loading, setLoading] = useState(false);
    const msal = useMsal();
    const sdk = getZaplifySdk(apiEndpoint, msal.instance);

    const handleViewChange = (view: UploadCompaniesBreadCrumbs) => setCurrentView(view);

    const handleActionStateChange = (newState: ActionState) => setActionState(newState);

    const handleUpdateSelectedColumn = (name: string | null) => setSelectedColumn(name);

    const handleResetSelectedColumn = () => setSelectedColumn(null);

    const handleUpdateCsvData = (data: { [key: string]: any }[]) => setCsvData(data);

    const handleSetSelectedCompanies = (options: CompanyAutocompleteOption[]) => setSelectedCompanies(options);

    const handleUpdateSelectedCompany = (newCompany: CompanyAutocompleteOption, idx: number) => {
        const temp = [...selectedCompanies];
        temp.splice(idx, 1, newCompany);
        setSelectedCompanies(temp);
    };

    const handleDeleteValidatedCompany = (companyName: string) => {
        const idx = selectedCompanies.findIndex((v) => v.name === companyName);
        const temp = [...selectedCompanies];
        temp.splice(idx, 1);
        setSelectedCompanies(temp);
    };

    const getValidationForCompanies = async () => {
        if (!selectedColumn) {
            return;
        }

        try {
            setActionState(ActionState.DISABLED);
            const companiesNames = csvData.map((d) => {
                if (!d[selectedColumn]) {
                    return;
                }

                return d[selectedColumn].toString().toLowerCase();
            });
            const sourcesSdk = sdk.profiles.sources;
            const allValidatedObjectCompanies = await sourcesSdk.validateNames(companiesNames);
            const uniqueSuggestions = removeSuggestionDuplicates(allValidatedObjectCompanies);
            const tempSelectedCompanies = uniqueSuggestions.map((s) => ({
                name: s.suggestion?.name || s.queryString,
                domain: s.suggestion?.website || '',
                industry: s.suggestion?.industry || '',
                id: s.suggestion?.id || '',
                isValid: !!s.suggestion,
            }));

            handleSetSelectedCompanies(tempSelectedCompanies);
            handleLoadingChange(false);
            setActionState(ActionState.ENABLED);
        } catch (error) {
            dispatch(setNotification("Couldn't validate companies names", 'error'));

            if (error instanceof Error && error.message) {
                console.error(error.message);
            }
        }
    };

    const removeSuggestionDuplicates = (validationNames: ValidatedName[]): ValidatedName[] => {
        return Array.from(new Set(validationNames.map((validatedName) => validatedName.queryString))).map((name) => {
            return validationNames.find((validatedName) => validatedName.queryString === name);
        }) as ValidatedName[];
    };

    const resetAllValues = () => {
        setCurrentView(UploadCompaniesBreadCrumbs.UPLOAD_LIST);
        setActionState(ActionState.DISABLED);
        setCsvData([]);
        setSelectedColumn(null);
        setSelectedCompanies([]);
    };

    const handleLoadingChange = (value: boolean) => setLoading(value);

    return (
        <UploadCompaniesContext.Provider
            value={{
                currentView,
                actionState,
                selectedColumn,
                csvData,
                selectedCompanies,
                isWaitingForValidation,
                loading,
                handleDeleteValidatedCompany,
                handleViewChange,
                handleActionStateChange,
                handleUpdateSelectedColumn,
                handleResetSelectedColumn,
                handleUpdateCsvData,
                handleUpdateSelectedCompany,
                getValidationForCompanies,
                resetAllValues,
                handleLoadingChange,
            }}
        >
            {children}
        </UploadCompaniesContext.Provider>
    );
};
